import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../context/AuthContext';
import { openwaAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/ui/page-header';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { StatutBadge } from '../components/ui/StatutBadge';
import SurveillanceWhatsapp from '../components/SurveillanceWhatsapp';
import { libelle } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatNombre } from '../lib/formats';
import { SESSIONS_SAINES } from '../lib/surveillanceWhatsapp';

/**
 * Envoi des alertes par WhatsApp, sur son propre écran.
 *
 * Cet écran était la moitié haute de « Fournisseurs GPS », sans rapport avec
 * lui: les boîtiers qui donnent la position d'un camion et le canal qui prévient
 * les gens sont deux intégrations distinctes, donc deux écrans.
 */

const HEALTHY_SESSION = SESSIONS_SAINES;
// La surveillance automatique bouge toute seule: l'écran la relit à ce rythme
// tant que l'onglet est visible (et dès qu'on y revient).
const DELAI_ACTUALISATION_MS = 30000;
const OUTBOX_STATES = ['pending', 'accepted', 'delivered', 'dead'];

// Le dernier message reçu par l'assistant (réservé au SUPER_ADMIN): il attend un
// expéditeur identifié par son numéro et une date lisible.
const formatMessageRecuOk = (shape) => {
  if (!shape) return false;
  const senderResolved = shape.from_suffix === 'c.us' || shape.has_sender_phone;
  const epoch = ['epoch_s', 'epoch_ms'].includes(shape.timestamp_kind);
  return Boolean(senderResolved && epoch);
};

const describeInboundShape = (shape) => {
  if (!shape) return null;
  return formatMessageRecuOk(shape) ? 'Format reconnu' : 'Format inattendu';
};

const inboundShapeTone = (shape) => {
  if (!shape) return 'neutral';
  return formatMessageRecuOk(shape) ? 'positive' : 'attention';
};

const inboundShapeHint = (shape, receivedAt) => {
  if (!shape) {
    return receivedAt
      ? `Détail non conservé · ${formatDateHeure(receivedAt)}`
      : 'Aucun message reçu conservé (7 jours)';
  }
  const parts = [`Numéro de l’expéditeur ${shape.has_sender_phone ? 'présent' : 'absent'}`];
  if (receivedAt) parts.push(formatDateHeure(receivedAt));
  return parts.join(' · ');
};

// Nature d'un message de la file d'envoi, en mots simples.
const natureMessage = (type) => {
  const t = String(type || '');
  if (t === 'integration_test') return 'Message d’essai';
  if (t.startsWith('customs_passage')) return 'Passage en douane';
  if (t.startsWith('trip_status')) return 'Changement d’étape du trajet';
  if (t.startsWith('bot_reply')) return 'Réponse de l’assistant';
  return libelle('alerteEvent', t, 'Alerte');
};

export default function OpenWaPage() {
  const { isSuperAdmin } = useAuth();
  const [health, setHealth] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [totalEnvois, setTotalEnvois] = useState(0);
  // Les envois se lisent page par page: le serveur donne le total, la page vit dans l'adresse.
  const pagination = usePaginationServeur(totalEnvois, { cle: 'envois' });
  const { page, taille } = pagination;
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);

  // Horloge de l'écran: « il y a 40 s » doit vieillir même si une lecture échoue.
  const [maintenant, setMaintenant] = useState(() => Date.now());

  const nouvelleDemande = useDerniereDemande();
  const load = useCallback(async ({ silencieux = false } = {}) => {
    const perimee = nouvelleDemande();
    try {
      const [healthData, outboxData] = await Promise.all([
        openwaAPI.health(),
        openwaAPI.outboxPage({ page, limit: taille }),
      ]);
      if (perimee()) return;
      setHealth(healthData);
      setOutbox(outboxData?.items || []);
      setTotalEnvois(outboxData?.total ?? (outboxData?.items || []).length);
      setMaintenant(Date.now());
    } catch (error) {
      if (perimee()) return;
      // Une lecture automatique qui échoue ne dérange pas: on garde l'écran tel quel.
      if (!silencieux) toast.error(messageErreur(error, 'Impossible de lire l’état de l’envoi WhatsApp'));
    }
  }, [page, taille, nouvelleDemande]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const actualiser = () => {
      if (!visible()) return;
      setMaintenant(Date.now());
      load({ silencieux: true });
    };
    const minuterie = setInterval(actualiser, DELAI_ACTUALISATION_MS);
    document.addEventListener('visibilitychange', actualiser);
    return () => {
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', actualiser);
    };
  }, [load]);

  const sendTest = async () => {
    setSending(true);
    try {
      await openwaAPI.sendTest(testPhone ? { recipient_phone: testPhone } : {});
      toast.success('Test placé dans la file d’envoi');
      await load();
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de créer le message d’essai'));
    } finally {
      setSending(false);
    }
  };

  const retry = async (item) => {
    try {
      await openwaAPI.retry(item.id);
      toast.success('Alerte remise en attente');
      await load();
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de relancer cette alerte'));
    }
  };

  const ready = health?.status === 'ready';
  const sessionOk = HEALTHY_SESSION.includes(health?.provider?.status);

  // Ce qui manque, en phrases simples (les messages techniques sont illisibles).
  const problemes = [];
  if (health) {
    if (!health.enabled) problemes.push({ court: 'Envoi désactivé', texte: 'L’envoi d’alertes WhatsApp est désactivé pour votre société.' });
    if (!health.configured) problemes.push({ court: 'Réglage incomplet', texte: 'Le réglage est incomplet: adresse du service, clé d’accès ou session manquante.' });
    if (!health.worker?.fresh) problemes.push({ court: 'Service muet', texte: 'Le service d’envoi ne donne aucun signe de vie récent.' });
    if ((health.diagnostics || []).some((d) => /Aucun numéro WhatsApp/.test(d))) {
      problemes.push({ court: 'Aucun numéro actif', texte: 'Aucun numéro WhatsApp actif dans les profils de cette organisation.' });
    }
    if (health.provider?.status && !sessionOk && health.provider.status !== 'not_checked') {
      problemes.push({ court: 'Session non prête', texte: 'La session WhatsApp n’est pas prête: reliez de nouveau le numéro par code QR.' });
    }
  }

  // Les quatre conditions pour qu'un message parte. Quand rien n'arrive, la seule
  // question utile est: laquelle est au rouge ?
  const gates = [
    {
      label: 'Activation',
      ok: health?.enabled,
      value: health?.enabled ? 'Activée' : 'Désactivée',
      hint: 'Interrupteur général',
    },
    {
      label: 'Configuration',
      ok: health?.configured,
      value: health?.configured ? 'Complète' : 'Incomplète',
      hint: 'Adresse du service, clé d’accès et session',
    },
    {
      label: 'Service d’envoi',
      ok: health?.worker?.fresh && health?.worker?.enabled,
      value: health?.worker?.fresh
        ? health?.worker?.enabled
          ? 'Actif'
          : 'Désactivé'
        : 'Injoignable',
      hint: 'Le programme qui envoie les messages de la file',
    },
    {
      label: 'Session WhatsApp',
      ok: sessionOk,
      value: sessionOk ? 'Connectée' : health?.provider?.status && health.provider.status !== 'not_checked' ? 'Non connectée' : 'Non vérifiée',
      hint: 'Numéro WhatsApp relié par code QR',
    },
  ];

  // L'assistant répond par la même file d'envoi, mais il n'est pas une des
  // quatre conditions: un assistant éteint est un choix, pas une panne, donc
  // il reste neutre au lieu de passer au rouge.
  const statsPlus = health?.bot ? (
    <dl className="space-y-2 text-sm">
      <div>
        <dt className="text-muted-foreground">Assistant WhatsApp</dt>
        <dd className="font-medium">{health.bot.enabled ? 'Activé' : 'Désactivé'}</dd>
        <dd className="text-xs text-muted-foreground">Répond aux messages des opérateurs</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">Numéros liés</dt>
        <dd className="font-medium tabular-nums">{formatNombre(health.bot.linked_count || 0)}</dd>
        <dd className="text-xs text-muted-foreground">
          {health.bot.last_used_at
            ? `Dernière utilisation ${formatDateHeure(health.bot.last_used_at)}`
            : 'Aucune utilisation'}
        </dd>
      </div>
      {isSuperAdmin && (
        <div>
          <dt className="text-muted-foreground">Dernier message reçu</dt>
          <dd className={inboundShapeTone(health.bot.last_inbound_shape) === 'attention' ? 'font-medium text-flame-ink' : 'font-medium'}>
            {describeInboundShape(health.bot.last_inbound_shape) || 'Aucun'}
          </dd>
          <dd className="text-xs text-muted-foreground">
            {inboundShapeHint(health.bot.last_inbound_shape, health.bot.last_inbound_at)}
          </dd>
        </div>
      )}
    </dl>
  ) : undefined;

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Alertes WhatsApp"
        description="Quatre conditions doivent être réunies pour qu’une alerte parte. Cette page dit laquelle manque."
        stats={gates.map((gate) => ({
          label: gate.label,
          valeur: gate.value,
          ton: gate.ok ? 'positif' : 'critique',
          aide: gate.hint,
        }))}
        statsPlus={statsPlus}
        alertesSousTitre
        alertes={problemes.map((item) => ({ niveau: 'attention', court: item.court, texte: item.texte }))}
        actions={
          <>
            <Badge variant={ready ? 'success' : 'warning'}>
              {ready ? 'Opérationnel' : 'Attention requise'}
            </Badge>
            <Button variant="outline" onClick={() => load()}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Rafraîchir
            </Button>
          </>
        }
      />

      <SurveillanceWhatsapp watchdog={health?.watchdog} maintenant={maintenant} />

      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            Envoyer un message d’essai
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 lg:grid-cols-[1fr_auto]">
          <Input
            value={testPhone}
            onChange={(event) => setTestPhone(event.target.value)}
            placeholder="Numéro test facultatif, ex. 22376123456"
            aria-label="Numéro de test"
          />
          <Button
            onClick={sendTest}
            disabled={sending || !ready}
            title={!ready ? 'Le service d’envoi doit être opérationnel avant un envoi.' : undefined}
          >
            <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            {sending ? 'Envoi…' : ready ? 'Envoyer un essai' : 'Service d’envoi indisponible'}
          </Button>
        </CardContent>
      </Card>

      <Card className="card-base">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Messages à envoyer</CardTitle>
          <div className="flex flex-wrap gap-2">
            {OUTBOX_STATES.map((state) => (
              <Badge key={state} variant="muted">
                {libelle('whatsappStatut', state)} : {formatNombre(health?.counts?.[state] || 0)}
              </Badge>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  {['Créée le', 'Message', 'Destinataire', 'État', 'Tentatives', 'Problème', ''].map(
                    (heading) => (
                      <th key={heading} className="readout-label p-3">
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {outbox.map((item) => (
                  <tr key={item.id}>
                    <td className="p-3">
                      {formatDateHeure(item.created_at)}
                    </td>
                    <td className="p-3 font-medium">{natureMessage(item.alert_type)}</td>
                    <td className="p-3 font-mono">{item.recipient_phone}</td>
                    <td className="p-3">
                      <StatutBadge domaine="whatsappStatut" code={item.status} />
                    </td>
                    <td className="p-3 tabular-nums">{item.attempts}</td>
                    <td className="max-w-xs truncate p-3 text-destructive">
                      {item.last_error ? 'L’envoi a échoué' : '—'}
                    </td>
                    <td className="p-3">
                      {['failed', 'dead'].includes(item.status) && (
                        <Button size="sm" variant="outline" onClick={() => retry(item)}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                          Relancer
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!outbox.length && (
                  <tr>
                    <td colSpan="7" className="p-6 text-center text-muted-foreground">
                      Aucun message d’alerte pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <BarrePagination pagination={pagination} nom="messages" className="mt-3" />
        </CardContent>
      </Card>
    </div>
  );
}
